import React, { useState, useEffect } from 'react';
import {
    Button,
    Input,
    Select,
    Card,
    Typography,
    Space,
    Tooltip,
    message,
    Row,
    Col,
    Statistic,
    Tag,
    Progress,
    Empty
} from 'antd';
import Table from 'components/Common/Table';
import {
    CopyOutlined,
    LinkOutlined,
    DeleteOutlined,
    FacebookOutlined,
    InstagramOutlined,
    LinkedinOutlined,
    TwitterOutlined,
    WhatsAppOutlined,
    MailOutlined,
    GoogleOutlined,
    MessageOutlined,
    GlobalOutlined,
    EyeOutlined,
    UserAddOutlined,
    RiseOutlined
} from '@ant-design/icons';
import * as actions from 'store/actions/AuthActions';

const { Text } = Typography;

// Platform icons mapping
const platformIcons = {
    facebook: <FacebookOutlined style={{ color: '#1877F2' }} />,
    instagram: <InstagramOutlined style={{ color: '#E4405F' }} />,
    linkedin: <LinkedinOutlined style={{ color: '#0A66C2' }} />,
    twitter: <TwitterOutlined style={{ color: '#1DA1F2' }} />,
    whatsapp: <WhatsAppOutlined style={{ color: '#25D366' }} />,
    email: <MailOutlined style={{ color: '#EA4335' }} />,
    google_ads: <GoogleOutlined style={{ color: '#4285F4' }} />,
    facebook_ads: <FacebookOutlined style={{ color: '#1877F2' }} />,
    sms: <MessageOutlined style={{ color: '#6B7280' }} />,
    custom: <GlobalOutlined style={{ color: '#6B7280' }} />,
};

const UTMLinkGenerator = ({ campaignId, campaignName }) => {
    const [baseUrl, setBaseUrl] = useState('https://teson.in/get-a-demo');
    const [selectedPlatforms, setSelectedPlatforms] = useState([]);
    const [utmLinks, setUtmLinks] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [platformChoices, setPlatformChoices] = useState([]);

    useEffect(() => {
        if (campaignId) {
            loadUTMLinks();
            loadAnalytics();
        }
    }, [campaignId]);

    const loadUTMLinks = async () => {
        setLoading(true);
        try {
            const response = await actions.getCampaignUTMLinks(campaignId);
            if (response) {
                setUtmLinks(response.links || []);
                setPlatformChoices(response.platform_choices || []);
            }
        } catch (error) {
            console.error('Error loading UTM links:', error);
        }
        setLoading(false);
    };

    const loadAnalytics = async () => {
        try {
            const response = await actions.getCampaignUTMAnalytics(campaignId);
            if (response) {
                setAnalytics(response);
            }
        } catch (error) {
            console.error('Error loading UTM analytics:', error);
        }
    };

    const handleGenerateLinks = async () => {
        if (!baseUrl) {
            message.error('Please enter a base URL');
            return;
        }
        if (selectedPlatforms.length === 0) {
            message.error('Please select at least one platform');
            return;
        }

        setGenerating(true);
        try {
            const response = await actions.generateCampaignUTMLinks(campaignId, {
                base_url: baseUrl,
                platforms: selectedPlatforms
            });
            if (response && response.success) {
                message.success(`Generated ${response.links.length} UTM links`);
                loadUTMLinks();
                loadAnalytics();
                setSelectedPlatforms([]);
            }
        } catch (error) {
            message.error('Failed to generate links');
            console.error('Error generating UTM links:', error);
        }
        setGenerating(false);
    };

    const handleCopyLink = (link) => {
        navigator.clipboard.writeText(link);
        message.success('Link copied to clipboard');
    };

    const handleDeleteLink = async (linkId) => {
        try {
            const response = await actions.deleteCampaignUTMLink(campaignId, linkId);
            if (response && response.success) {
                message.success('Link deleted');
                loadUTMLinks();
                loadAnalytics();
            }
        } catch (error) {
            message.error('Failed to delete link');
            console.error('Error deleting UTM link:', error);
        }
    };

    const columns = [
        {
            title: 'Platform',
            dataIndex: 'platform',
            key: 'platform',
            width: 120,
            render: (platform, record) => (
                <Space>
                    {platformIcons[platform] || <GlobalOutlined />}
                    <Text>{record.platform_display}</Text>
                </Space>
            ),
        },
        {
            title: 'UTM Link',
            dataIndex: 'full_url',
            key: 'full_url',
            ellipsis: true,
            render: (url) => (
                <Tooltip title={url}>
                    <Text copyable={{ text: url }} style={{ fontSize: 12 }}>
                        {url.length > 60 ? url.substring(0, 60) + '...' : url}
                    </Text>
                </Tooltip>
            ),
        },
        {
            title: 'Visits',
            dataIndex: 'total_visits',
            key: 'total_visits',
            width: 80,
            align: 'center',
            render: (visits) => (
                <Tag color="blue">{visits || 0}</Tag>
            ),
        },
        {
            title: 'Leads',
            dataIndex: 'total_leads',
            key: 'total_leads',
            width: 80,
            align: 'center',
            render: (leads) => (
                <Tag color="green">{leads || 0}</Tag>
            ),
        },
        {
            title: 'Conv. Rate',
            key: 'conversion',
            width: 100,
            align: 'center',
            render: (_, record) => {
                const rate = record.total_visits > 0
                    ? ((record.total_leads / record.total_visits) * 100).toFixed(1)
                    : 0;
                return <Text>{rate}%</Text>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Copy Link">
                        <Button
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopyLink(record.full_url)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteLink(record.id)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    // Get platforms that don't have links yet
    const availablePlatforms = platformChoices.filter(
        p => !utmLinks.find(link => link.platform === p.value)
    );

    return (
        <div>
            {/* Analytics Summary */}
            {analytics && analytics.summary && (
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="Total Visits"
                                value={analytics.summary.total_visits}
                                prefix={<EyeOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="Total Leads"
                                value={analytics.summary.total_leads}
                                prefix={<UserAddOutlined />}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card>
                            <Statistic
                                title="Conversion Rate"
                                value={analytics.summary.conversion_rate}
                                suffix="%"
                                prefix={<RiseOutlined />}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Link Generator */}
            <Card
                title={<><LinkOutlined /> Generate UTM Links</>}
                style={{ marginBottom: 16 }}
            >
                <Row gutter={16}>
                    <Col span={10}>
                        <Text strong>Landing Page URL</Text>
                        <Input
                            placeholder="https://teson.in/get-a-demo"
                            value={baseUrl}
                            onChange={(e) => setBaseUrl(e.target.value)}
                            style={{ marginTop: 8 }}
                        />
                    </Col>
                    <Col span={10}>
                        <Text strong>Platforms</Text>
                        <Select
                            mode="multiple"
                            style={{ width: '100%', marginTop: 8 }}
                            placeholder="Select platforms"
                            value={selectedPlatforms}
                            onChange={setSelectedPlatforms}
                            options={availablePlatforms}
                        />
                    </Col>
                    <Col span={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Button
                            type="primary"
                            icon={<LinkOutlined />}
                            onClick={handleGenerateLinks}
                            loading={generating}
                            disabled={!baseUrl || selectedPlatforms.length === 0}
                            style={{ width: '100%' }}
                        >
                            Generate
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* UTM Links Table */}
            <Card title={<><LinkOutlined /> Campaign Links ({utmLinks.length})</>}>
                {utmLinks.length > 0 ? (
                    <Table
                        dataSource={utmLinks}
                        columns={columns}
                        rowKey="id"
                        loading={loading}
                        pagination={false}
                        size="small"
                    />
                ) : (
                    <Empty
                        description="No UTM links generated yet. Generate links above to start tracking campaign traffic."
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                )}
            </Card>

            {/* Platform Performance */}
            {analytics && analytics.by_platform && analytics.by_platform.length > 0 && (
                <Card title="Performance by Platform" style={{ marginTop: 16 }}>
                    {analytics.by_platform.map((platform, index) => (
                        <div key={index} style={{ marginBottom: 16 }}>
                            <Row align="middle" style={{ marginBottom: 8 }}>
                                <Col span={4}>
                                    <Space>
                                        {platformIcons[platform.platform] || <GlobalOutlined />}
                                        <Text strong>{platform.platform_label}</Text>
                                    </Space>
                                </Col>
                                <Col span={16}>
                                    <Progress
                                        percent={platform.conversion_rate}
                                        size="small"
                                        format={(percent) => `${percent}% conversion`}
                                    />
                                </Col>
                                <Col span={4} style={{ textAlign: 'right' }}>
                                    <Text type="secondary">
                                        {platform.visits} visits / {platform.leads} leads
                                    </Text>
                                </Col>
                            </Row>
                        </div>
                    ))}
                </Card>
            )}
        </div>
    );
};

export default UTMLinkGenerator;
